import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { useCallback, useMemo } from 'react'
import {
  AddressAutoComplete,
  type Option,
} from '~/components/admin/fields/property/AddressAutocomplete'
import { Button } from '~/components/ui'
import { useGeocodeSearch } from '~/hooks'

type MapAddressSearchFilterProps = {
  addressSearch?: string
  updateFilter: (updates: Record<string, any>) => void
  autoScrollIntoView?: boolean
}

export default function MapAddressSearchFilter({
  addressSearch,
  updateFilter,
  autoScrollIntoView,
}: MapAddressSearchFilterProps) {
  const placesLibrary = useMapsLibrary('places')
  const geocodingLibrary = useMapsLibrary('geocoding')
  const isGooglePlacesReady = Boolean(placesLibrary && geocodingLibrary)
  const {
    setSearch: setLocationSearch,
    predictions,
    setPredictions,
    getPlaceData,
  } = useGeocodeSearch({ enabled: isGooglePlacesReady })

  const options: Option[] = useMemo(
    () =>
      predictions.map((prediction) => ({
        value: prediction.place_id,
        label: prediction.description,
        description: prediction.description,
      })),
    [predictions]
  )

  const handleSelectOption = useCallback(
    async (option: Option) => {
      const prediction = predictions.find((p) => p.place_id === option.value)
      if (prediction) {
        const placeData = await getPlaceData(prediction)
        if (placeData !== null) {
          const updates: Record<string, any> = {
            addressSearch: placeData.address,
            cities: [],
            counties: [],
            postalCodes: [],
            states: [],
          }

          if (placeData.city && placeData.city.trim()) {
            updates.cities = [placeData.city.trim()]
          }

          if (
            updates.cities.length === 0 &&
            placeData.county &&
            placeData.county.trim()
          ) {
            const countyName = placeData.county
              .replace(/county/gi, '')
              .replace(/\s+/g, ' ')
              .trim()
            if (countyName) {
              updates.counties = [countyName]
            }
          }

          if (
            updates.cities.length === 0 &&
            updates.counties.length === 0 &&
            placeData.postalCode &&
            placeData.postalCode.trim()
          ) {
            updates.postalCodes = [placeData.postalCode.trim()]
          }

          if (placeData.stateAbbr && placeData.stateAbbr.trim()) {
            updates.states = [placeData.stateAbbr.trim().toUpperCase()]
          }

          updateFilter(updates)
        }
      }

      setPredictions([])
    },
    [
      predictions,
      getPlaceData,
      updateFilter,
      setPredictions,
    ]
  )

  const handleInputChange = useCallback(
    (value: string) => {
      setLocationSearch(value)
      updateFilter({ addressSearch: value })
    },
    [setLocationSearch, updateFilter]
  )

  return (
    <div className="space-y-2">
      <p className="text-sm">
        Address Search
        {(addressSearch ?? '').length > 0 && (
          <Button
            variant="link"
            size="none"
            className="ml-2"
            onClick={() => {
              updateFilter({ addressSearch: undefined })
            }}>
            (reset)
          </Button>
        )}
      </p>
      <div className="relative">
        <AddressAutoComplete
          options={options}
          address={addressSearch ?? ''}
          onSelectOption={handleSelectOption}
          onInputChange={handleInputChange}
          placeholder="Search by address..."
          autoScrollIntoView={autoScrollIntoView}
        />
      </div>
    </div>
  )
}
